import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import LandingLayout from './LandingLayout';
import { Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import api from '../../api';

export default function SignUp() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const packageId = useMemo(() => {
    const q = searchParams.get('package')?.trim();
    if (q) return q;
    return location.state?.packageId || undefined;
  }, [searchParams, location.state?.packageId]);

  const billingCycle = useMemo(() => {
    const raw =
      searchParams.get('cycle') ||
      searchParams.get('billingCycle') ||
      location.state?.billingCycle;
    return raw === 'annual' ? 'annual' : 'monthly';
  }, [searchParams, location.state?.billingCycle]);

  const [step, setStep] = useState(1); // 1: Account, 2: Review & create, 3: Success
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [packageLoading, setPackageLoading] = useState(false);
  const [packageError, setPackageError] = useState('');
  const [packageFetchNonce, setPackageFetchNonce] = useState(0);

  const [formData, setFormData] = useState({
    // Account info
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
    // Business info
    businessName: '',
    businessEmail: '',
    businessPhone: '',
  });

  useEffect(() => {
    if (!packageId) return;
    let cancelled = false;
    const load = async () => {
      setPackageLoading(true);
      setPackageError('');
      try {
        const response = await api.get(`/public/packages/${packageId}`);
        if (!cancelled) setSelectedPackage(response.data.package);
      } catch (err) {
        console.error('Error fetching package:', err);
        if (!cancelled) {
          setSelectedPackage(null);
          setPackageError(
            err.response?.data?.error || err.message || 'Could not load this plan. Please try again.'
          );
        }
      } finally {
        if (!cancelled) setPackageLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [packageId, packageFetchNonce]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const validateStep1 = () => {
    if (!formData.email || !formData.password || !formData.confirmPassword || 
        !formData.firstName || !formData.lastName || !formData.businessName) {
      setError('Please fill in all required fields');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (step === 1) {
      if (!validateStep1()) return;
      if (packageLoading || packageError || !selectedPackage) {
        setError('Plan details must load before you continue. Use Retry if loading failed.');
        return;
      }
      setStep(2);
    } else if (step === 2) {
      await handleCreateAccount();
    }
  };

  const handleCreateAccount = async () => {
    const resolvedPackageId = selectedPackage?.id || packageId;
    if (!resolvedPackageId) {
      setError('Please choose a plan on the pricing page before signing up.');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const signupData = {
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        businessName: formData.businessName,
        businessEmail: formData.businessEmail || formData.email,
        businessPhone: formData.businessPhone || formData.phone,
        packageId: resolvedPackageId,
        billingCycle,
      };

      const response = await api.post('/public/signup', signupData);

      if (response.data.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
      } else {
        setStep(3);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Sign up failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const price = selectedPackage
    ? (billingCycle === 'annual' ? selectedPackage.price_annual : selectedPackage.price_monthly)
    : 0;

  if (!packageId) {
    return (
      <LandingLayout>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
              <AlertCircle className="h-12 w-12 text-orange-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Choose a plan first
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Start from the pricing page to pick a subscription plan. Your selection is kept when you continue to sign up.
              </p>
              <Link
                to="/pricing"
                className="inline-flex px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-semibold"
              >
                View pricing
              </Link>
            </div>
          </div>
        </div>
      </LandingLayout>
    );
  }

  return (
    <LandingLayout>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div className={`flex items-center ${step >= 1 ? 'text-orange-600 dark:text-orange-500' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                  step >= 1 ? 'border-orange-600 dark:border-orange-500 bg-orange-600 dark:bg-orange-500 text-white' : 'border-gray-300 dark:border-gray-700'
                }`}>
                  {step > 1 ? <CheckCircle className="h-5 w-5" /> : '1'}
                </div>
                <span className="ml-2 font-medium">Account</span>
              </div>
              <div className={`flex-1 h-0.5 mx-4 ${step >= 2 ? 'bg-orange-600 dark:bg-orange-500' : 'bg-gray-300 dark:bg-gray-700'}`} />
              <div className={`flex items-center ${step >= 2 ? 'text-orange-600 dark:text-orange-500' : 'text-gray-400'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                  step >= 2 ? 'border-orange-600 dark:border-orange-500 bg-orange-600 dark:bg-orange-500 text-white' : 'border-gray-300 dark:border-gray-700'
                }`}>
                  {step > 2 ? <CheckCircle className="h-5 w-5" /> : '2'}
                </div>
                <span className="ml-2 font-medium">Review</span>
              </div>
            </div>
          </div>

          {/* Success Step */}
          {step === 3 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Welcome to SparxMate!
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Your account has been created. You can now start using SparxMate.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-semibold"
              >
                Sign In
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                {step === 1 ? 'Create Your Account' : 'Review and create your account'}
              </h2>

              {packageError && step === 1 && (
                <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <p className="text-amber-800 dark:text-amber-200 text-sm">{packageError}</p>
                  <button
                    type="button"
                    onClick={() => setPackageFetchNonce((n) => n + 1)}
                    className="shrink-0 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium"
                  >
                    Retry
                  </button>
                </div>
              )}

              {error && (
                <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start">
                  <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mr-2 mt-0.5" />
                  <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
                </div>
              )}

              {step === 1 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        First Name *
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Business Name *
                    </label>
                    <input
                      type="text"
                      name="businessName"
                      value={formData.businessName}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Password *
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Confirm Password *
                    </label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      required
                    />
                  </div>
                </div>
              )}

              {step === 2 && packageLoading && (
                <div className="flex flex-col items-center justify-center py-12 text-gray-600 dark:text-gray-400">
                  <Loader2 className="h-10 w-10 animate-spin text-orange-600 mb-3" />
                  <p className="text-sm">Loading plan details…</p>
                </div>
              )}

              {step === 2 && !packageLoading && packageError && (
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <p className="text-amber-800 dark:text-amber-200 text-sm">{packageError}</p>
                  <button
                    type="button"
                    onClick={() => setPackageFetchNonce((n) => n + 1)}
                    className="shrink-0 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium"
                  >
                    Retry
                  </button>
                </div>
              )}

              {step === 2 && !packageLoading && selectedPackage && (
                <div className="space-y-4">
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Subscription Summary
                    </h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Plan:</span>
                        <span className="text-gray-900 dark:text-white font-medium">{selectedPackage.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Billing:</span>
                        <span className="text-gray-900 dark:text-white font-medium capitalize">{billingCycle}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Trial:</span>
                        <span className="text-gray-900 dark:text-white font-medium">{selectedPackage.trial_days} days free</span>
                      </div>
                      <div className="border-t border-gray-200 dark:border-gray-600 pt-2 mt-2">
                        <div className="flex justify-between">
                          <span className="text-lg font-semibold text-gray-900 dark:text-white">Total:</span>
                          <span className="text-lg font-bold text-orange-600 dark:text-orange-500">£{Number(price).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    No payment is taken here. You can add payment details later under{' '}
                    <strong className="text-gray-800 dark:text-gray-200">Settings → Subscription</strong> before your trial ends.
                    After your trial, your subscription continues at the price shown unless you cancel.
                  </p>
                </div>
              )}

              <div className="mt-6 flex justify-between">
                {step > 1 && (
                  <button
                    type="button"
                    onClick={() => setStep(step - 1)}
                    className="px-6 py-2 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Back
                  </button>
                )}
                <button
                  type="submit"
                  disabled={
                    loading ||
                    packageLoading ||
                    !!packageError ||
                    !selectedPackage
                  }
                  className="ml-auto px-6 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {step === 1 ? 'Continue' : 'Create account'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </LandingLayout>
  );
}


